import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/Table";
import { Gear, FloppyDisk } from "@phosphor-icons/react";

const NAV_ITEMS = [
  ["/tracking", "Live Tracking"], ["/vehicles", "Vehicles"], ["/customers", "Customers"],
  ["/devices", "Device Inventory"], ["/reports", "Reports"], ["/graphics", "Graphics"],
  ["/maintenance", "Maintenance"], ["/renewals", "Renewals"],
];

export default function Settings() {
  const [r, setR] = useState(null);

  useEffect(() => { api.get("/settings/rules").then((x) => setR({ labels: {}, hidden_paths: [], ...x.data })).catch(() => {}); }, []);

  const save = async () => {
    try {
      await api.put("/settings/rules", {
        idle_speed: Number(r.idle_speed) || 0, stop_minutes: Number(r.stop_minutes) || 0,
        inactive_hours: Number(r.inactive_hours) || 0, labels: r.labels || {}, hidden_paths: r.hidden_paths || [],
      });
      toast.success("Settings saved — reload to see nav changes");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!r) return <div className="p-6 text-muted-foreground">Loading…</div>;

  const toggleHidden = (p) => setR((s) => ({ ...s, hidden_paths: s.hidden_paths.includes(p) ? s.hidden_paths.filter((x) => x !== p) : [...s.hidden_paths, p] }));
  const setLabel = (p, v) => setR((s) => ({ ...s, labels: { ...s.labels, [p]: v } }));
  const inp = "bg-background border border-border rounded-sm px-2 py-2 text-sm w-full";

  return (
    <div className="p-5 space-y-4 max-w-4xl">
      <PageHeader title="Settings & Rules" subtitle="Device thresholds, menu labels & screen access"
        actions={<button data-testid="settings-save" onClick={save} className="flex items-center gap-1.5 bg-primary text-white text-sm font-semibold px-4 py-2 rounded-sm"><FloppyDisk size={15} weight="bold" /> Save</button>} />

      <div className="bg-card border border-border rounded-sm p-4 space-y-3">
        <div className="label-caps flex items-center gap-1.5"><Gear size={13} /> Device Rules / Thresholds</div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className="label-caps text-[9px]">Idle speed ≤ (km/h)</label><input data-testid="rule-idle" type="number" min="0" value={r.idle_speed} onChange={(e) => setR({ ...r, idle_speed: e.target.value })} className={inp} /></div>
          <div><label className="label-caps text-[9px]">Stopped after (min)</label><input data-testid="rule-stop" type="number" min="0" value={r.stop_minutes} onChange={(e) => setR({ ...r, stop_minutes: e.target.value })} className={inp} /></div>
          <div><label className="label-caps text-[9px]">Inactive after (hours)</label><input data-testid="rule-inactive" type="number" min="0" value={r.inactive_hours} onChange={(e) => setR({ ...r, inactive_hours: e.target.value })} className={inp} /></div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-sm p-4 space-y-2">
          <div className="label-caps">Rename Menu Labels</div>
          <p className="text-xs text-muted-foreground">Leave blank to keep the default.</p>
          {NAV_ITEMS.map(([p, def]) => (
            <div key={p} className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-28 shrink-0">{def}</span>
              <input data-testid={`label-${p.slice(1)}`} value={r.labels?.[p] || ""} onChange={(e) => setLabel(p, e.target.value)} placeholder={def} className={inp} />
            </div>
          ))}
        </div>

        <div className="bg-card border border-border rounded-sm p-4 space-y-2">
          <div className="label-caps">Screen Access (hide from customer / sub-users)</div>
          <p className="text-xs text-muted-foreground">Checked screens are hidden from non-admin logins.</p>
          {NAV_ITEMS.map(([p, def]) => (
            <label key={p} className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid={`hide-${p.slice(1)}`} checked={(r.hidden_paths || []).includes(p)} onChange={() => toggleHidden(p)} />
              {r.labels?.[p] || def}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
